import { useState } from 'react'
import type { ReactNode } from 'react'
import { Input } from '@/components/ui/Input'
import {
  selectClasses, textareaClasses, labelClasses, submitButtonClasses, capitalize, fieldError,
} from './formStyles'
import { POS_OPTIONS, GENDER_OPTIONS } from '@/types/customFlashcard'
import type { WordSuggestion } from '@/types/word'

interface SuggestionReviewFormProps {
  initial: WordSuggestion
  submitting: boolean
  errors: Record<string, string[]>
  onSubmit: (suggestion: WordSuggestion) => void
  footer?: ReactNode
}

/** Lets the learner correct an AI suggestion before it enters the lexicon. The parent owns the API call. */
export function SuggestionReviewForm({ initial, submitting, errors, onSubmit, footer }: SuggestionReviewFormProps) {
  const [lemma, setLemma] = useState(initial.lemma)
  const [translations, setTranslations] = useState(initial.translations.join(', '))
  const [partOfSpeech, setPartOfSpeech] = useState(initial.part_of_speech)
  const [gender, setGender] = useState(initial.gender ?? '')
  const [definition, setDefinition] = useState(initial.definition)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSubmit({
      lemma: lemma.trim(),
      part_of_speech: partOfSpeech,
      gender: gender || null,
      translations: translations.split(',').map((t) => t.trim()).filter(Boolean),
      definition: definition.trim(),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <Input
        id="suggestion-lemma"
        label="Word"
        value={lemma}
        onChange={(e) => setLemma(e.target.value)}
        error={fieldError(errors, 'lemma')}
        required
      />
      <Input
        id="suggestion-translations"
        label="Translations"
        value={translations}
        onChange={(e) => setTranslations(e.target.value)}
        error={fieldError(errors, 'translations')}
        aria-describedby="suggestion-translations-hint"
        required
      />
      <p id="suggestion-translations-hint" className="-mt-2 text-xs text-muted font-light">
        Separate several with commas.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="suggestion-pos" className={labelClasses}>Part of speech</label>
          <select
            id="suggestion-pos"
            value={partOfSpeech}
            onChange={(e) => setPartOfSpeech(e.target.value)}
            className={selectClasses}
            required
          >
            <option value="">—</option>
            {POS_OPTIONS.map((pos) => (
              <option key={pos} value={pos}>{capitalize(pos)}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="suggestion-gender" className={labelClasses}>Gender</label>
          <select
            id="suggestion-gender"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className={selectClasses}
          >
            <option value="">—</option>
            {GENDER_OPTIONS.map((g) => (
              <option key={g} value={g}>{capitalize(g)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="suggestion-definition" className={labelClasses}>Definition</label>
        <textarea
          id="suggestion-definition"
          value={definition}
          onChange={(e) => setDefinition(e.target.value)}
          rows={2}
          className={textareaClasses}
        />
      </div>

      {errors.base && (
        <p role="alert" className="text-xs text-error font-light">{errors.base[0]}</p>
      )}

      <div className="flex items-center justify-between gap-3 mt-1">
        {footer}
        <button type="submit" disabled={submitting} className={submitButtonClasses}>
          {submitting ? 'Adding…' : 'Add word'}
        </button>
      </div>
    </form>
  )
}
